import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import Button from "../../components/ui/Button";
import { Star, Users, TrendingUp, MessageSquareQuote, Quote, Calendar, Mail, ArrowRight } from "lucide-react";


const TestimonialsPage = () => {
  const heroRef = useRef(null);
  const footerRef = useRef(null);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const y = useSpring(useTransform(scrollYProgress, [0, 1], [50, -50]), springConfig);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const testimonials = [
    {
      id: 1,
      quote: "IT Services transformed our digital infrastructure completely. Their team's expertise in cloud solutions saved us 40% in operational costs while improving system reliability.",
      clientName: "Sarah Johnson",
      company: "Nexus Financial",
      industry: "Finance",
      logo: "https://images.unsplash.com/photo-1563694983011-6f4d90358083?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
      rating: 5
    },
    {
      id: 2,
      quote: "The cybersecurity implementation by IT Services helped us prevent what could have been a devastating data breach. Their proactive approach to security is unmatched.",
      clientName: "Michael Chen",
      company: "HealthPlus",
      industry: "Healthcare",
      logo: "https://images.pexels.com/photos/5699516/pexels-photo-5699516.jpeg?auto=compress&cs=tinysrgb&w=256",
      rating: 5
    },
    {
      id: 3,
      quote: "Working with IT Services on our e-commerce platform redesign resulted in a 28% increase in conversion rates within just two months of launch.",
      clientName: "Emma Rodriguez",
      company: "Urban Outfitters",
      industry: "Retail",
      logo: "https://images.unsplash.com/photo-1534452203293-494d7ddbf7e0?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
      rating: 5
    },
    {
      id: 4,
      quote: "The custom CRM solution developed by IT Services has streamlined our sales process and improved customer retention by 35%.",
      clientName: "David Wilson",
      company: "Velocity Sales",
      industry: "Sales",
      logo: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=256",
      rating: 4
    },
    {
      id: 5,
      quote: "IT Services' managed IT support has been a game-changer for our small business. We now have enterprise-level IT without the overhead costs.",
      clientName: "Jennifer Lee",
      company: "Creative Solutions",
      industry: "Marketing",
      logo: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80",
      rating: 5
    },
    {
      id: 6,
      quote: "The data analytics dashboard IT Services built for us has transformed how we make business decisions. We now have real-time insights that drive our strategy.",
      clientName: "Robert Taylor",
      company: "DataDrive",
      industry: "Technology",
      logo: "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=256",
      rating: 4
    }
  ];

  const duplicatedTestimonials = [...testimonials, ...testimonials];

  return (
    <div className="flex flex-col min-h-screen bg-slate-50" ref={containerRef}>
      <Header heroSectionRef={heroRef} footerSectionRef={footerRef} variant="default" />

      <main className="flex-grow" id="main-content">
        {/* Hero Section - FLUID TYPOGRAPHY APPLIED */}
        <section ref={heroRef} className="relative h-[400px] 3xl:h-[500px] flex items-center justify-center overflow-hidden mt-24 mx-6 rounded-[2.5rem] shadow-2xl shadow-blue-900/20">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80&w=2000"
              alt="Client Success Stories"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-blue-900/20" />
            <div className="absolute inset-0 bg-gradient-to-r from-blue-800/90 via-blue-700/50 to-transparent" />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2 3xl:px-6 3xl:py-3 rounded-full mb-6 3xl:mb-8 border border-white/20 shadow-lg"
            >
              <MessageSquareQuote className="w-4 h-4 3xl:w-5 3xl:h-5 text-blue-300" />
              <span className="text-white text-[clamp(0.625rem,1.5vw,0.75rem)] 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-bold uppercase tracking-wider">
                Trusted by Industry Leaders
              </span>
            </motion.div>

            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="text-[clamp(2rem,8vw,4.5rem)] 3xl:text-7xl font-bold text-white mb-8 3xl:mb-10 tracking-tight drop-shadow-lg"
            >
              Client Success Stories
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Link
                to="/submit-review"
                className="inline-flex items-center gap-2 bg-white text-blue-900 hover:bg-blue-50 font-bold px-8 py-3 3xl:px-10 3xl:py-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <MessageSquareQuote className="w-5 h-5 3xl:w-6 3xl:h-6" />
                <span className="text-sm 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl">Share Your Experience</span>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section - FLUID TYPOGRAPHY APPLIED */}
        <section ref={sectionRef} className="relative py-16 md:py-20 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
          {/* Background grid */}
          <motion.div
            style={{ y, opacity: 0.05 }}
            className="absolute inset-0"
          >
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="testimonials-grid" width="8" height="8" patternUnits="userSpaceOnUse">
                  <path d="M 8 0 L 0 0 0 8" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-300" />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#testimonials-grid)" />
            </svg>
          </motion.div>

          {/* Floating orbs */}
          <motion.div
            style={{ y: useTransform(scrollYProgress, [0, 1], [0, 100]) }}
            className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            style={{ y: useTransform(scrollYProgress, [0, 1], [0, -100]) }}
            className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-br from-pink-400 to-orange-400 rounded-full mix-blend-multiply filter blur-3xl opacity-15"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            {/* Section Header */}
            <motion.div
              className="mb-12 3xl:mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-start gap-4 mb-6 3xl:mb-8">
                <motion.div
                  className="flex-shrink-0 w-16 h-16 3xl:w-20 3xl:h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 15 }}
                >
                  <Users className="w-8 h-8 3xl:w-10 3xl:h-10 text-white" strokeWidth={2.5} />
                </motion.div>

                <div className="flex-grow">
                  <h2 className="text-[clamp(1.5rem,5vw,2.25rem)] 3xl:text-5xl font-bold text-gray-900 mb-3 3xl:mb-4 leading-tight tracking-tight">
                    What Our Clients Say
                  </h2>
                  <p className="text-[clamp(0.875rem,2.5vw,1.125rem)] 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl text-gray-600 leading-relaxed max-w-3xl 3xl:max-w-4xl" style={{ lineHeight: '1.7' }}>
                    Read authentic feedback from our clients across various industries. Their experiences highlight our commitment to delivering exceptional IT services and solutions.
                  </p>
                </div>
              </div>

              {/* Stats Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 3xl:mt-14 3xl:gap-10"
              >
                <div className="bg-white rounded-2xl p-6 3xl:p-8 shadow-md border border-gray-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 3xl:w-12 3xl:h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                      <Star className="w-5 h-5 3xl:w-6 3xl:h-6 text-yellow-600" />
                    </div>
                    <h3 className="text-[clamp(1.25rem,3vw,1.5rem)] 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl font-bold text-gray-900">4.8/5</h3>
                  </div>
                  <p className="text-[clamp(0.75rem,2vw,0.875rem)] 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-600 font-medium">
                    Average Client Rating
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 3xl:p-8 shadow-md border border-gray-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 3xl:w-12 3xl:h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-5 h-5 3xl:w-6 3xl:h-6 text-blue-600" />
                    </div>
                    <h3 className="text-[clamp(1.25rem,3vw,1.5rem)] 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl font-bold text-gray-900">{testimonials.length}+</h3>
                  </div>
                  <p className="text-[clamp(0.75rem,2vw,0.875rem)] 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-600 font-medium">
                    Happy Clients
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-6 3xl:p-8 shadow-md border border-gray-100">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 3xl:w-12 3xl:h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 3xl:w-6 3xl:h-6 text-green-600" />
                    </div>
                    <h3 className="text-[clamp(1.25rem,3vw,1.5rem)] 3xl:text-3xl 4xl:text-4xl 5xl:text-5xl 6xl:text-6xl font-bold text-gray-900">95%</h3>
                  </div>
                  <p className="text-[clamp(0.75rem,2vw,0.875rem)] 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-600 font-medium">
                    Client Retention Rate
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Auto-scrolling Testimonials Carousel */}
            <div className="relative py-10 3xl:py-16">
              <div className="overflow-hidden">
                <motion.div
                  className="flex gap-6 3xl:gap-10"
                  animate={{
                    x: [0, -2520],
                  }}
                  transition={{
                    x: {
                      duration: 40,
                      repeat: Infinity,
                      ease: "linear",
                    },
                  }}
                >
                  {duplicatedTestimonials.map((testimonial, index) => (
                    <div
                      key={`${testimonial.id}-${index}`}
                      className="flex-shrink-0 w-[420px] 3xl:w-[500px] bg-white rounded-2xl p-8 3xl:p-10 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
                    >
                      {/* Quote Icon */}
                      <div className="mb-6 3xl:mb-8">
                        <Quote className="w-10 h-10 3xl:w-12 3xl:h-12 text-blue-500 opacity-50" />
                      </div>

                      {/* Testimonial Text */}
                      <p className="text-[clamp(0.875rem,2.5vw,1rem)] 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl text-gray-700 leading-relaxed mb-8 3xl:mb-10 min-h-[100px] 3xl:min-h-[120px]">
                        {testimonial.quote}
                      </p>

                      {/* Rating Stars */}
                      <div className="flex gap-1 mb-6 3xl:mb-8">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 3xl:w-5 3xl:h-5 ${i < testimonial.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`}
                          />
                        ))}
                      </div>

                      {/* Author Info */}
                      <div className="flex items-center gap-4 pt-6 3xl:pt-8 border-t border-gray-100">
                        <img
                          src={testimonial.logo}
                          alt={testimonial.clientName}
                          className="w-12 h-12 3xl:w-16 3xl:h-16 rounded-full object-cover ring-2 ring-blue-100"
                        />
                        <div>
                          <h4 className="font-bold text-gray-900 text-[clamp(0.75rem,2vw,0.875rem)] 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl">
                            {testimonial.clientName}
                          </h4>
                          <p className="text-[clamp(0.625rem,1.5vw,0.75rem)] 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl text-gray-500">
                            {testimonial.company}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* Gradient Overlays for fade effect */}
              <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-slate-50 via-blue-50/30 to-transparent pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-slate-50 via-blue-50/30 to-transparent pointer-events-none" />
            </div>
          </div>
        </section>

        {/* Contact CTA Section - FLUID TYPOGRAPHY APPLIED */}
        <section className="relative py-16 md:py-20 3xl:py-32 bg-white overflow-hidden">
          {/* Subtle background pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="cta-grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <circle cx="5" cy="5" r="1" fill="currentColor" className="text-gray-400" />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#cta-grid)" />
            </svg>
          </div>

          {/* Gradient orbs for visual interest */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-br from-pink-200 to-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" />

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl 3xl:max-w-[1800px] 5xl:max-w-[2500px] 6xl:max-w-[3400px]">
            <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 rounded-3xl p-10 md:p-12 3xl:p-20 shadow-2xl relative overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 3xl:w-96 3xl:h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 3xl:w-72 3xl:h-72 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                <div className="text-center mb-8 3xl:mb-12">
                  <motion.div
                    className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 3xl:px-6 3xl:py-3 rounded-full mb-5 3xl:mb-8"
                    initial={{ scale: 0.9, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                  >
                    <MessageSquareQuote className="w-4 h-4 3xl:w-5 3xl:h-5 text-white" />
                    <span className="text-white text-[clamp(0.625rem,1.5vw,0.75rem)] 3xl:text-base 4xl:text-lg 5xl:text-xl 6xl:text-2xl font-bold uppercase tracking-wider">
                      Get Started Today
                    </span>
                  </motion.div>

                  <h2 className="text-[clamp(1.75rem,6vw,3rem)] 3xl:text-6xl font-bold text-white mb-4 3xl:mb-6 leading-tight">
                    Ready to Transform Your Business?
                  </h2>
                  <p className="text-[clamp(1rem,3vw,1.25rem)] 3xl:text-2xl 4xl:text-3xl 5xl:text-4xl 6xl:text-5xl text-blue-100 max-w-2xl 3xl:max-w-4xl mx-auto leading-relaxed">
                    Join hundreds of satisfied clients who have elevated their technology infrastructure with our expert IT services.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 3xl:gap-6 justify-center items-center mb-10 3xl:mb-16">
                  <motion.a
                    href="tel:7777077482"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-white text-blue-600 hover:bg-gray-100 font-bold px-8 py-4 3xl:px-10 3xl:py-5 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl rounded-xl shadow-lg group flex items-center gap-2 cursor-pointer"
                  >
                    <Calendar className="w-5 h-5 3xl:w-6 3xl:h-6" />
                    Schedule a Consultation
                    <ArrowRight className="w-5 h-5 3xl:w-6 3xl:h-6 group-hover:translate-x-1 transition-transform" />
                  </motion.a>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.location.href = '/contact-us'}
                    className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-bold px-8 py-4 3xl:px-10 3xl:py-5 3xl:text-xl 4xl:text-2xl 5xl:text-3xl 6xl:text-4xl rounded-xl flex items-center gap-2"
                  >
                    <Mail className="w-5 h-5 3xl:w-6 3xl:h-6" />
                    Contact Us
                  </motion.button>
                </div>

                {/* Trust indicators */}
                <motion.div
                  className="pt-8 3xl:pt-12 border-t border-white/20"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 3xl:gap-12 text-center">
                    <div>
                      <div className="text-[clamp(1.5rem,4vw,1.875rem)] 3xl:text-4xl 4xl:text-5xl 5xl:text-6xl 6xl:text-7xl font-bold text-white mb-1">24/7</div>
                      <div className="text-[clamp(0.75rem,2vw,0.875rem)] 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl text-blue-100">
                        Support Available
                      </div>
                    </div>

                    <div>
                      <div className="text-[clamp(1.5rem,4vw,1.875rem)] 3xl:text-4xl 4xl:text-5xl 5xl:text-6xl 6xl:text-7xl font-bold text-white mb-1">10+</div>
                      <div className="text-[clamp(0.75rem,2vw,0.875rem)] 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl text-blue-100">
                        Years Experience
                      </div>
                    </div>

                    <div>
                      <div className="text-[clamp(1.5rem,4vw,1.875rem)] 3xl:text-4xl 4xl:text-5xl 5xl:text-6xl 6xl:text-7xl font-bold text-white mb-1">99.9%</div>
                      <div className="text-[clamp(0.75rem,2vw,0.875rem)] 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl text-blue-100">
                        Uptime Guarantee
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer ref={footerRef} variant="full" />
    </div>
  );
};

export default TestimonialsPage;
